import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { EmojiPickerButton, EmojiReactions } from '@/components/EmojiPicker';
import { FileSelector, AttachmentsList } from '@/components/FileUpload';
import { Folder, FolderPlus, MoveRight, Check, CheckCircle, Mail, Send, Trash2, Edit, User, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { useState, useEffect } from 'react';
import MentionAutocomplete from '@/components/MentionAutocomplete';
import MentionText from '@/components/MentionText';
import { AIEmailHelper } from '@/components/AIHelpers';
import { SpeechToTextIcon } from '@/components/SpeechToText';
import TranslateButton from '@/components/TranslateButton';
import { Pagination } from '@/components/Pagination';

export const AdminMessagesTab = ({
  user,
  messageFolderFormRef,
  messageFolders,
  fetchMessageFolders,
  messageFolderForm,
  setMessageFolderForm,
  editingMessageFolder,
  handleCreateMessageFolder,
  handleUpdateMessageFolder,
  handleDeleteMessageFolder,
  handleEditMessageFolder,
  resetMessageFolderForm,
  messageForm,
  setMessageForm,
  selectedUsers,
  setSelectedUsers,
  toggleUserSelection,
  selectAllUsers,
  deselectAllUsers,
  adminSelectedFiles,
  setAdminSelectedFiles,
  users,
  fetchUsers,
  handleSendMessage,
  resetMessageForm,
  selectedFolder,
  setSelectedFolder,
  messages,
  fetchMessages,
  selectedMessages,
  toggleMessageSelection,
  selectAllMessages,
  deselectAllMessages,
  handleBulkDelete,
  handleMoveToFolder,
  replyingTo,
  setReplyingTo,
  replyText,
  setReplyText,
  handleReplyToMessage,
  handleResolveMessage,
  handleDeleteMessage,
  handleReactToMessage
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const PER_PAGE = 25;
  // Reset page when folder filter changes
  useEffect(() => { setPage(1); }, [selectedFolder]);
  
  const getFilteredMessages = () => {
    let filtered = messages;
    
    // Filter by folder first
    if (selectedFolder === 'unassigned') {
      filtered = filtered.filter(m => !m.folder_id);
    } else if (selectedFolder !== null) {
      filtered = filtered.filter(m => m.folder_id === selectedFolder);
    }
    
    // Then filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(m => 
        m.subject?.toLowerCase().includes(query) ||
        m.message?.toLowerCase().includes(query) ||
        m.admin_reply?.toLowerCase().includes(query) ||
        m.sender_name?.toLowerCase().includes(query) ||
        m.sender_email?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  };

  const filteredMessages = getFilteredMessages();

  return (
    <>
          {/* Folder Management Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Folder className="w-5 h-5" />
                Message Folders
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button onClick={fetchMessageFolders} variant="outline" size="sm">
                Load Folders
              </Button>

              {/* Folder Form */}
              <div ref={messageFolderFormRef} className="border rounded-lg p-4 space-y-3">
                <h4 className="font-medium">{editingMessageFolder ? 'Edit Folder' : 'Create New Folder'}</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  <Input
                    placeholder="Folder name"
                    value={messageFolderForm.name}
                    onChange={(e) => setMessageFolderForm({ ...messageFolderForm, name: e.target.value })}
                  />
                  <Input
                    placeholder="Description (optional)"
                    value={messageFolderForm.description}
                    onChange={(e) => setMessageFolderForm({ ...messageFolderForm, description: e.target.value })}
                  />
                  <Input
                    type="color"
                    value={messageFolderForm.color}
                    onChange={(e) => setMessageFolderForm({ ...messageFolderForm, color: e.target.value })}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={editingMessageFolder ? handleUpdateMessageFolder : handleCreateMessageFolder}
                    size="sm"
                    disabled={!messageFolderForm.name}
                  >
                    <FolderPlus className="w-4 h-4 mr-1" />
                    {editingMessageFolder ? 'Update' : 'Create'} Folder
                  </Button>
                  {editingMessageFolder && (
                    <Button onClick={resetMessageFolderForm} variant="outline" size="sm">
                      Cancel
                    </Button>
                  )}
                </div>
              </div>

              {/* Folders List */}
              {messageFolders.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium">Your Folders:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {messageFolders.map(folder => (
                      <div
                        key={folder.id}
                        className="border rounded p-3 flex items-center justify-between"
                        style={{ borderLeft: `4px solid ${folder.color}` }}
                      >
                        <div>
                          <p className="font-medium">{folder.name}</p>
                          {folder.description && (
                            <p className="text-sm text-gray-500">{folder.description}</p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditMessageFolder(folder)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteMessageFolder(folder.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Send Message Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                Send Message to Users
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Load users button */}
              {users.length === 0 && (
                <Button onClick={fetchUsers} className="w-full">
                  <User className="w-4 h-4 mr-2" />
                  Load Users
                </Button>
              )}
              
              {users.length > 0 && (
                <>
                  {/* User Selection */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Select Recipients</Label>
                      <div className="space-x-2">
                        <Button size="sm" variant="outline" onClick={selectAllUsers}>
                          Select All
                        </Button>
                        <Button size="sm" variant="outline" onClick={deselectAllUsers}>
                          Deselect All
                        </Button>
                      </div>
                    </div>
                    <div className="border rounded-lg p-4 max-h-48 overflow-y-auto space-y-2">
                      {users.map(user => (
                        <div key={user.id} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(user.id)}
                            onChange={() => toggleUserSelection(user.id)}
                            className="rounded"
                          />
                          <span className="text-sm">
                            {user.username} ({user.email}) - {user.membership_type}
                          </span>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      {selectedUsers.length} user(s) selected
                    </p>
                  </div>

                  {/* Subject */}
                  <div>
                    <Label>Subject</Label>
                    <Input
                      value={messageForm.subject}
                      onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                      placeholder="Message subject"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label>Message</Label>
                      <AIEmailHelper 
                        recipientName={selectedUsers.length === 1 ? users.find(u => u.id === selectedUsers[0])?.full_name : 'Members'}
                        onInsert={(text) => setMessageForm({ ...messageForm, message: text })}
                      />
                    </div>
                    <div className="relative">
                      <MentionAutocomplete
                        value={messageForm.message}
                        onChange={(value) => setMessageForm({ ...messageForm, message: value })}
                        placeholder="Type your message here... (Type @username to mention someone)"
                        rows={6}
                        className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="absolute bottom-2 right-2">
                        <SpeechToTextIcon 
                          onTranscript={(text) => setMessageForm({ ...messageForm, message: messageForm.message + ' ' + text })}
                        />
                      </div>
                    </div>
                    <div className="mt-2">
                      <EmojiPickerButton 
                        onEmojiSelect={(emoji) => setMessageForm({ ...messageForm, message: messageForm.message + emoji })}
                      />
                    </div>
                  </div>

                  {/* Priority */}
                  <div>
                    <Label>Priority</Label>
                    <Select
                      value={messageForm.priority}
                      onValueChange={(value) => setMessageForm({ ...messageForm, priority: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Send Notification Toggle */}
                  <div className="flex items-center justify-between">
                    <Label>Send in-app notification</Label>
                    <Switch
                      checked={messageForm.send_notification}
                      onCheckedChange={(checked) => 
                        setMessageForm({ ...messageForm, send_notification: checked })
                      }
                    />
                  </div>

                  {/* File Attachments */}
                  <div>
                    <Label>Attachments (Optional)</Label>
                    <FileSelector
                      selectedFiles={adminSelectedFiles}
                      onFilesSelected={setAdminSelectedFiles}
                      multiple={true}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button onClick={handleSendMessage} className="flex-1">
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </Button>
                    <Button variant="outline" onClick={resetMessageForm}>
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Messages List */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between mb-4">
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  All Messages
                </CardTitle>
                <Button onClick={fetchMessages} variant="outline" size="sm">
                  Refresh
                </Button>
              </div>
              
              {/* Search Bar */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Search by subject, content, sender name or email..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                  className="pl-10 pr-10"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {searchQuery && (
                <p className="text-sm text-gray-500 mb-3">
                  Found {filteredMessages.length} message{filteredMessages.length !== 1 ? 's' : ''} matching &ldquo;{searchQuery}&rdquo;
                </p>
              )}
              
              {/* Folder Filter */}
              {messageFolders.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant={selectedFolder === null ? 'default' : 'outline'}
                    onClick={() => setSelectedFolder(null)}
                  >
                    All ({messages.length})
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedFolder === 'unassigned' ? 'default' : 'outline'}
                    onClick={() => setSelectedFolder('unassigned')}
                  >
                    Unassigned ({messages.filter(m => !m.folder_id).length})
                  </Button>
                  {messageFolders.map(folder => (
                    <Button
                      key={folder.id}
                      size="sm"
                      variant={selectedFolder === folder.id ? 'default' : 'outline'}
                      onClick={() => setSelectedFolder(folder.id)}
                      style={{
                        borderLeft: `3px solid ${folder.color}`
                      }}
                    >
                      {folder.name} ({messages.filter(m => m.folder_id === folder.id).length})
                    </Button>
                  ))}
                </div>
              )}
              
              {/* Bulk Actions */}
              {messages.length > 0 && (
                <div className="flex items-center gap-2 pt-3 border-t">
                  <Button size="sm" variant="outline" onClick={selectAllMessages}>
                    Select All ({filteredMessages.length})
                  </Button>
                  <Button size="sm" variant="outline" onClick={deselectAllMessages}>
                    Deselect All
                  </Button>
                  {selectedMessages.length > 0 && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={handleBulkDelete}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete Selected ({selectedMessages.length})
                    </Button>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent>
              {getFilteredMessages().length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Mail className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No messages in this view. {messages.length === 0 ? 'Click Refresh to load messages.' : 'Try a different folder filter.'}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {getFilteredMessages().slice((page - 1) * PER_PAGE, page * PER_PAGE).map(message => (
                    <div
                      key={message.id}
                      className={`border rounded-lg p-4 ${
                        message.status === 'open' ? 'border-yellow-400 bg-yellow-50' :
                        message.status === 'replied' ? 'border-blue-400 bg-blue-50' :
                        'border-gray-300 bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={selectedMessages.includes(message.id)}
                            onChange={() => toggleMessageSelection(message.id)}
                            className="mt-1"
                          />
                          <div>
                            <h4 className="font-semibold">{message.subject}</h4>
                            <p className="text-sm text-gray-600">
                              From: {message.username} | Priority: {message.priority} | 
                              Status: <span className="font-medium">{message.status}</span>
                            </p>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(message.created_at).toLocaleString()}
                        </div>
                      </div>
                      <div className="text-sm mb-3 whitespace-pre-wrap">
                        <MentionText>{message.message}</MentionText>
                      </div>
                      {message.message && (
                        <TranslateButton text={message.message} className="mb-2" />
                      )}
                      
                      {message.admin_reply && (
                        <div className="mt-3 pt-3 border-t border-gray-300">
                          <p className="text-sm font-medium text-blue-600 mb-1">Admin Reply:</p>
                          <div className="text-sm whitespace-pre-wrap">
                            <MentionText>{message.admin_reply}</MentionText>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Replied: {new Date(message.replied_at).toLocaleString()}
                          </p>
                          <TranslateButton text={message.admin_reply} className="mt-2" />
                        </div>
                      )}
                      
                      {/* Attachments */}
                      {message.attachments && message.attachments.length > 0 && (
                        <div className="mt-3 pt-3 border-t">
                          <AttachmentsList attachments={message.attachments} />
                        </div>
                      )}
                      
                      {/* Emoji Reactions */}
                      <div className="mt-3 pt-3 border-t">
                        <EmojiReactions
                          reactions={message.reactions || {}}
                          onReact={(emoji) => handleReactToMessage(message.id, emoji)}
                          currentUserId={user.id}
                        />
                      </div>
                      
                      {/* Reply Form */}
                      {replyingTo === message.id && (
                        <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
                          <div className="mb-2">
                            <label className="text-sm font-medium text-gray-700">Reply to {message.username}:</label>
                          </div>
                          <div className="relative">
                            <MentionAutocomplete
                              value={replyText}
                              onChange={setReplyText}
                              placeholder="Type your reply... Use @ to mention users"
                              className="w-full min-h-[80px] mb-2 pr-12"
                            />
                            <div className="absolute bottom-4 right-2">
                              <SpeechToTextIcon 
                                onTranscript={(text) => setReplyText(replyText + ' ' + text)}
                              />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => {
                                if (replyText.trim()) {
                                  handleReplyToMessage(message.id, replyText);
                                }
                              }}
                              disabled={!replyText.trim()}
                            >
                              Send Reply
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setReplyingTo(null);
                                setReplyText('');
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex gap-2 mt-3">
                        {message.status === 'open' && !replyingTo && (
                          <Button
                            size="sm"
                            onClick={() => {
                              setReplyingTo(message.id);
                              setReplyText('');
                            }}
                          >
                            Reply
                          </Button>
                        )}
                        {message.status !== 'resolved' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResolveMessage(message.id)}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Mark Resolved
                          </Button>
                        )}
                        {messageFolders.length > 0 && (
                          <Select
                            value={message.folder_id || 'unassigned'}
                            onValueChange={(value) => handleMoveToFolder(message.id, value === 'unassigned' ? null : value)}
                          >
                            <SelectTrigger className="w-40 h-8">
                              <MoveRight className="w-4 h-4 mr-1" />
                              <SelectValue placeholder="Move to..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unassigned">Unassigned</SelectItem>
                              {messageFolders.map(folder => (
                                <SelectItem key={folder.id} value={folder.id}>
                                  {folder.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteMessage(message.id)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <Pagination
                page={page}
                totalPages={Math.max(1, Math.ceil(getFilteredMessages().length / PER_PAGE))}
                onChange={setPage}
                testidPrefix="admin-messages-pagination"
                theme="light"
              />
            </CardContent>
          </Card>
    </>
  );
};

export default AdminMessagesTab;
